import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, CheckCircle2, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import PageHeader from '../../components/settings/PageHeader';
import SettingCard from '../../components/settings/SettingCard';
import { useAuth } from '../../hooks/useAuth';
import { useSettingsStore } from '../../store/settingsStore';
import { validatePassword, validateMatch, validateRequired } from '../../utils/validators';
import { friendlyAuthError } from '../../utils/authErrors';

const inputClass =
  'w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-900/10 focus:border-neutral-900 transition-all text-sm';

/** Rough password strength 0–3 for the meter. */
function strength(pw: string): number {
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/\d/.test(pw) && /[^A-Za-z0-9]/.test(pw)) score++;
  return score;
}

export default function ChangePassword() {
  const navigate = useNavigate();
  const { reauthenticate, updatePassword } = useAuth();
  const { saveSecurity } = useSettingsStore();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const s = strength(next);
  const strengthLabel = ['Too weak', 'Weak', 'Good', 'Strong'][s];
  const strengthColor = ['bg-rose-400', 'bg-amber-400', 'bg-lime-500', 'bg-green-500'][s];

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const err =
      validateRequired(current, 'Current password') ??
      validatePassword(next) ??
      validateMatch(next, confirm);
    if (err) {
      setError(err);
      return;
    }
    if (next === current) {
      setError('New password must be different from the current one.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await reauthenticate(current);
      await updatePassword(next);
      await saveSecurity({ lastPasswordChange: new Date().toISOString() });
      setDone(true);
      setTimeout(() => navigate('/app/settings/security'), 1500);
    } catch (e2) {
      setError(friendlyAuthError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="space-y-6">
        <PageHeader title="Change password" icon={KeyRound} backTo="/app/settings/security" />
        <SettingCard>
          <div className="flex flex-col items-center text-center py-8">
            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <CheckCircle2 className="w-7 h-7 text-green-600" />
            </div>
            <h2 className="text-lg font-bold">Password updated</h2>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Taking you back to Security…</p>
          </div>
        </SettingCard>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-xl">
      <PageHeader title="Change password" subtitle="Confirm your current password, then set a new one" icon={KeyRound} backTo="/app/settings/security" />

      <SettingCard>
        <form className="space-y-5" onSubmit={submit}>
          <div>
            <label className="text-sm font-medium text-neutral-700">Current password</label>
            <div className="relative mt-1">
              <input type={show ? 'text' : 'password'} value={current} onChange={(e) => setCurrent(e.target.value)} className={inputClass + ' pr-11'} autoComplete="current-password" />
              <button type="button" onClick={() => setShow((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 dark:text-neutral-500 hover:text-neutral-700" aria-label={show ? 'Hide' : 'Show'}>
                {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-neutral-700">New password</label>
            <input type={show ? 'text' : 'password'} value={next} onChange={(e) => setNext(e.target.value)} className={inputClass + ' mt-1'} autoComplete="new-password" placeholder="At least 8 characters" />
            {next && (
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1 h-1.5 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
                  <div className={`h-full rounded-full transition-all ${strengthColor}`} style={{ width: `${((s + 1) / 4) * 100}%` }} />
                </div>
                <span className="text-xs text-neutral-500 dark:text-neutral-400 w-16 text-right">{strengthLabel}</span>
              </div>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-neutral-700">Confirm new password</label>
            <input type={show ? 'text' : 'password'} value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputClass + ' mt-1'} autoComplete="new-password" />
          </div>

          {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{error}</p>}

          <button type="submit" disabled={submitting} className="w-full bg-neutral-900 text-white py-3 rounded-xl font-semibold hover:bg-neutral-800 transition-colors disabled:opacity-60">
            {submitting ? 'Updating…' : 'Update password'}
          </button>
        </form>
      </SettingCard>

      <p className="flex items-center gap-2 text-xs text-neutral-400 dark:text-neutral-500 px-2">
        <ShieldCheck className="w-3.5 h-3.5" /> For your security you may be asked to sign in again.
      </p>
    </div>
  );
}
